import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Purchase, PurchaseItem, Traveler } from "types";
import shallow from "zustand/shallow";
import { PurchaseDrawer } from "@/components/Dashboard/Roster/RosterGuestList/PurchaseDrawer";
import { PurchaseContactInfoDrawer } from "@/components/PurchasesMenu/CustomerInformation/PurchaseContactInfoDrawer";
import { usePurchase } from "@/hooks/resources/usePurchase";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useResource } from "@/hooks/useResource";
import { useUpdateEffect } from "@/hooks/useUpdateEffect";
import { useModal } from "@/hooks/useVisibility";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";

export const PurchaseDrawerManager = () => {
    const [drawerPurchaseId, drawerPurchaseItemId, drawerContactId] = usePurchaseDrawerStore(
        (state) => [state.purchaseId, state.purchaseItemId, state.contactId],
        shallow,
    );
    const actions = usePurchaseDrawerStore().actions;
    const [selectedDrawerPurchase, setSelectedDrawerPurchase] = useState<Purchase>();
    const [selectedDrawerItem, setSelectedDrawerItem] = useState<PurchaseItem>();
    const [selectedDrawerContact, setSelectedDrawerContact] = useState<Traveler>();

    const { data: drawerPurchase } = usePurchase(drawerPurchaseId);
    const { data: drawerPurchaseItem } = useResource<PurchaseItem>(["purchaseItems", drawerPurchaseItemId]);
    const { data: drawerContact } = useTraveler(drawerContactId ?? undefined);

    const contactInfoDrawer = useModal(false);
    const purchaseDrawer = useModal(false);
    const { pathname } = useLocation();

    const handleContactInfoClick = useCallback(() => {
        contactInfoDrawer.show();
    }, [contactInfoDrawer]);

    const resetPurchaseDrawer = useCallback(() => {
        actions.setPurchaseId(null);
        actions.setPurchaseItemId(null);
        actions.setContactId(null);
        setSelectedDrawerContact(undefined);
        setSelectedDrawerPurchase(undefined);
        setSelectedDrawerItem(undefined);
    }, [actions]);

    const handleClosePurchaseDrawer = useCallback(() => {
        if (!contactInfoDrawer.isVisible) {
            resetPurchaseDrawer();
            purchaseDrawer.hide();
        }
    }, [purchaseDrawer, resetPurchaseDrawer, contactInfoDrawer]);

    const handleCloseContactInfo = useCallback(() => {
        contactInfoDrawer.hide();

        if (!purchaseDrawer.isVisible) {
            actions.setPurchaseId(null);
            actions.setPurchaseItemId(null);
            setSelectedDrawerPurchase(undefined);
            setSelectedDrawerItem(undefined);
            actions.setContactId(null);
            setSelectedDrawerContact(undefined);
        }
    }, [contactInfoDrawer, purchaseDrawer, actions]);

    useEffect(() => {
        if (!drawerPurchase) {
            return;
        }

        setSelectedDrawerPurchase(drawerPurchase);

        if (!drawerPurchaseItemId) {
            setSelectedDrawerItem(undefined);
            if (!purchaseDrawer.isVisible) {
                purchaseDrawer.show();
            }
            return;
        }

        if (drawerPurchaseItem) {
            setSelectedDrawerItem(drawerPurchaseItem);
            if (!purchaseDrawer.isVisible) {
                purchaseDrawer.show();
            }
        }
    }, [drawerPurchase, drawerPurchaseItem, drawerPurchaseItemId, purchaseDrawer]);

    useEffect(() => {
        if (drawerContact) {
            setSelectedDrawerContact(drawerContact);
        }
    }, [drawerContact]);

    // This manager is mounted for the whole app, so nothing tears the drawers down on navigation.
    useUpdateEffect(() => {
        if (!drawerPurchaseId && !drawerPurchaseItemId && !drawerContactId) {
            return;
        }
        resetPurchaseDrawer();
        purchaseDrawer.hide();
        contactInfoDrawer.hide();
    }, [pathname]);

    if (!drawerPurchaseId && !drawerPurchaseItemId) return null;

    return (
        <>
            <PurchaseDrawer
                selectedPurchase={selectedDrawerPurchase}
                selectedItem={selectedDrawerItem}
                isOpen={purchaseDrawer.isVisible}
                onContactInfoClick={handleContactInfoClick}
                onClose={handleClosePurchaseDrawer}
            />

            {selectedDrawerContact && (
                <PurchaseContactInfoDrawer
                    travelerId={selectedDrawerContact.id}
                    isOpen={contactInfoDrawer.isVisible}
                    onClose={handleCloseContactInfo}
                />
            )}
        </>
    );
};

export const openPurchaseDrawer = (purchaseId: ID, itemId: ID, contactId: ID) => {
    const actions = usePurchaseDrawerStore.getState().actions;
    actions.setContactId(contactId);
    actions.setPurchaseId(purchaseId);
    actions.setPurchaseItemId(itemId);
};
